import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import cn from 'classnames';
import { Button, Callout, Typography, useIsInBottomSheet } from 'seagull';
import { getTextColorsForBackground } from '~/business-logic/contrastLogic';
import { useCobBranding } from '~/client-flow/clientOb/CobBrandingContext';
import { useStoreActions } from '~/contexts';
import { PlaidMode, usePlaidState } from '~/hooks/usePlaidState';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import styles from './ConnectPlaid.module.scss';
import { mapErrorToMessage } from './errors';
import plaidLogo from './plaid-logo-horizontal-RGB.png';
/** Renders a UI that starts connects a bank account via plaid. */ export default function ConnectPlaid(props) {
    const { disabledReason, relationshipId, vendorId, customerId, onSuccess, doPaymentMethodAction } = props;
    const { t: pmTexts } = useLocaleKeys('components.managePaymentMethods');
    const { logBI } = useStoreActions();
    const { branding } = useCobBranding();
    const isInBottomSheet = useIsInBottomSheet();
    const useNewPlaidLayout = isInBottomSheet;
    const [errorMessage, setErrorMessage] = useState(null);
    const brandButtonStyle = useMemo(()=>{
        if (!useNewPlaidLayout) return undefined;
        const buttonPrimary = branding.buttonPrimary;
        const textOnFilled = getTextColorsForBackground(buttonPrimary).primary;
        return {
            '--button-primary': buttonPrimary,
            '--button-text-on-filled': textOnFilled
        };
    }, [
        useNewPlaidLayout,
        branding.buttonPrimary
    ]);
    const plaidLink = usePlaidState({
        mode: {
            type: PlaidMode.Customer,
            vendorId,
            customerId,
            relationshipId
        },
        onSuccess (action) {
            onSuccess(action, {
                type: 'add'
            });
        },
        onError (error) {
            setErrorMessage(mapErrorToMessage(error, pmTexts));
        }
    });
    function openPlaidLink() {
        logBI(10805);
        setErrorMessage(null);
        plaidLink.open();
    }
    return /*#__PURE__*/ _jsxs("form", {
        className: cn(styles.form, {
            [styles.formInSheet]: useNewPlaidLayout
        }),
        style: brandButtonStyle,
        "data-testid": "manage-payment-methods-connect-plaid-form",
        onSubmit: (event)=>{
            event.preventDefault();
            doPaymentMethodAction(openPlaidLink);
        },
        children: [
            useNewPlaidLayout ? /*#__PURE__*/ _jsxs(Typography, {
                variant: "paragraph1",
                as: "p",
                children: [
                    pmTexts.securelyConnectWithPlaid(),
                    ' ',
                    /*#__PURE__*/ _jsx("img", {
                        className: styles.plaidLogoInline,
                        src: plaidLogo,
                        alt: "Plaid"
                    })
                ]
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph1",
                        children: pmTexts.securelyAddBank()
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph1",
                        children: pmTexts.connectInstantly()
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: useNewPlaidLayout ? styles.footerInSheet : styles.footer,
                children: [
                    !useNewPlaidLayout && /*#__PURE__*/ _jsx("img", {
                        className: styles.plaidLogo,
                        src: plaidLogo,
                        alt: "Plaid logo"
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        bi: {
                            name: 'connect_bank_via_plaid'
                        },
                        type: "submit",
                        size: "small",
                        skin: "primary",
                        roundness: useNewPlaidLayout ? 'm' : undefined,
                        fluid: useNewPlaidLayout,
                        disabled: !!disabledReason,
                        testId: "manage-payment-methods-connect-plaid",
                        intent: Intent.AddPaymentMethod,
                        tooltip: disabledReason ? {
                            placement: 'top',
                            title: /*#__PURE__*/ _jsx("div", {
                                "data-testid": "connect-plaid-disabled-reason",
                                children: disabledReason
                            })
                        } : undefined,
                        children: pmTexts.connectBankAccount()
                    })
                ]
            }),
            errorMessage && /*#__PURE__*/ _jsx(Callout, {
                className: styles.error,
                intent: "error",
                testId: "manage-payment-methods-plaid-error",
                children: errorMessage
            })
        ]
    });
}
